import { useEffect, useRef } from "react";
import { getSocket } from "shared/services/socket";
import * as io from "socket.io-client";
import { Message } from "shared/types/chatBoard";
import { mapCommentToMessage, mergeAndSortMessages, sortRepliesChronologically } from "shared/utils/chatBoardUtils";

interface UseChatBoardSocketProps {
  chatBoardId?: string | number;
  userId?: string | number;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setReplies: React.Dispatch<React.SetStateAction<{ [commentId: string]: any[] }>>;
  setSelectedMessage: React.Dispatch<React.SetStateAction<Message | null>>;
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
}

export default function useChatBoardSocket({
  chatBoardId,
  userId,
  setMessages,
  setReplies,
  setSelectedMessage,
  messagesContainerRef,
}: UseChatBoardSocketProps) {
  const socketRef = useRef<io.Socket | null>(null);
  const isUnmountingRef = useRef(false);
  const currentChatBoardIdRef = useRef<string | number | undefined>(chatBoardId);
  const repliesRef = useRef<{ [commentId: string]: any[] }>({});
  const shouldIncrementCountRef = useRef(false);

  useEffect(() => {
    isUnmountingRef.current = false;
    const previousChatBoardId = currentChatBoardIdRef.current;
    currentChatBoardIdRef.current = chatBoardId;

    if (!userId || !chatBoardId) {
      return;
    }

    const socket = getSocket();
    socketRef.current = socket;

    if (!socket) {
      return;
    }

    const joinRoom = () => {
      if (socket && socket.connected && chatBoardId) {
        const roomData = {
          board_id: chatBoardId.toString(),
        };
        socket.emit("join_chat_board", roomData);
      } else {
      }
    };

    if (socket.connected) {
      joinRoom();
    } else {
      socket.once("connect", () => {
        joinRoom();
      });
    }

    socket.on("connect", () => {
    });

    socket.on("disconnect", (reason) => {
    });

    const handleReconnect = () => {
      joinRoom();
    };
    socket.on("reconnect", handleReconnect);

    const handleNewComment = (data: any) => {
      const commentData = data?.comment || data?.data || data;

      const receivedBoardId =
        data?.board_id?.toString() ||
        data?.chatBoardId?.toString() ||
        data?.chat_board_id?.toString() ||
        commentData?.board_id?.toString() ||
        commentData?.chat_board_id?.toString() ||
        commentData?.chatBoardId?.toString();

      const boardIdMatch = receivedBoardId === chatBoardId?.toString();

      if (boardIdMatch || !receivedBoardId) {
        if (!commentData) {
          return;
        }

        if (!commentData.comment_id && !commentData.id) {
          return;
        }

        const newMessage = mapCommentToMessage(commentData);

        setMessages((prev) => {
          const newMsgId = newMessage.id.toString();

          const existingIndex = prev.findIndex((msg) => {
            const msgId = msg.id.toString();
            return msgId === newMsgId;
          });

          if (existingIndex !== -1) {
            return prev;
          }

          const tempIndex = prev.findIndex((msg) => {
            const msgId = msg.id.toString();
            return (
              msgId.startsWith("temp-") &&
              msg.content === newMessage.content &&
              msg.sender.name === newMessage.sender.name
            );
          });

          if (tempIndex !== -1) {
            const updatedMessages = [...prev];
            updatedMessages[tempIndex] = newMessage;
            return updatedMessages;
          }

          const duplicateIndex = prev.findIndex((msg) => {
            const msgId = msg.id.toString();
            if (msgId.startsWith("temp-")) {
              return false;
            }
            return (
              msg.content === newMessage.content &&
              msg.sender.name === newMessage.sender.name &&
              msg.id.toString() !== newMsgId
            );
          });

          if (duplicateIndex !== -1) {
            const updatedMessages = [...prev];
            updatedMessages[duplicateIndex] = newMessage;
            return updatedMessages;
          }

          const updatedMessages = mergeAndSortMessages([...prev, newMessage]);

          setTimeout(() => {
            if (messagesContainerRef.current) {
              const container = messagesContainerRef.current;
              const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
              if (isNearBottom) {
                container.scrollTop = container.scrollHeight;
              }
            }
          }, 100);

          return updatedMessages;
        });
      }
    };

    const possibleNewCommentEvents = ["comment:created"];
    const possibleNewReplyEvents = ["reply:created"];

    const eventHandlers: { [key: string]: (data: any) => void } = {};

    possibleNewCommentEvents.forEach(eventName => {
      const handler = (data: any) => {
        handleNewComment(data);
      };
      eventHandlers[eventName] = handler;
      socket.on(eventName, handler);
    });

    const handleNewReply = (data: any) => {
      const replyData = data?.reply || data?.data || data;

      if (!replyData || (!replyData.reply_id && !replyData.id)) {
        return;
      }

      const commentId = 
        data?.comment_id?.toString() ||
        data?.commentId?.toString() ||
        replyData.comment_id?.toString() || 
        replyData.commentId?.toString() ||
        replyData.comment?.id?.toString() ||
        replyData.comment?.comment_id?.toString() ||
        replyData.parent_id?.toString() ||
        replyData.parentId?.toString();

      if (!commentId) {
        return;
      }

      const replyCreatedAt = replyData.created_at || replyData.createdAt || new Date().toISOString();
      const replyId = replyData.reply_id || replyData.id;
      
      const replyUserId = 
        replyData.user_id?.toString() ||
        replyData.userId?.toString() ||
        replyData.user?.user_id?.toString() ||
        replyData.user?.id?.toString() ||
        replyData.user?.userId?.toString();
      const currentUserId = userId?.toString();
      const isCurrentUserReply = replyUserId && currentUserId && replyUserId === currentUserId;

      shouldIncrementCountRef.current = false;
      setReplies((prevReplies) => {
        const existingReplies = prevReplies[commentId] || [];
        const normalizedReplyId = replyId ? String(replyId).trim() : null;
        const replyExistsInState = normalizedReplyId && existingReplies.some((r: any) => {
          const existingReplyId = r.reply_id ? String(r.reply_id).trim() : (r.id ? String(r.id).trim() : null);
          return existingReplyId && normalizedReplyId && existingReplyId === normalizedReplyId;
        });
        
        if (replyExistsInState) {
          const replyIndex = existingReplies.findIndex((r: any) => {
            const existingReplyId = r.reply_id ? String(r.reply_id).trim() : (r.id ? String(r.id).trim() : null);
            return existingReplyId && normalizedReplyId && existingReplyId === normalizedReplyId;
          });
          if (replyIndex !== -1) {
            const updatedReplies = [...existingReplies];
            updatedReplies[replyIndex] = replyData;
            const newReplies = {
              ...prevReplies,
              [commentId]: sortRepliesChronologically(updatedReplies),
            };
            repliesRef.current = newReplies;
            shouldIncrementCountRef.current = false;
            setSelectedMessage((prev) => {
              if (prev && prev.id.toString() === commentId) {
                return {
                  ...prev,
                  lastReplyAt: replyCreatedAt,
                };
              }
              return prev;
            });
            
            return newReplies;
          }
          shouldIncrementCountRef.current = false;
          repliesRef.current = prevReplies;
          return prevReplies;
        }
        
        shouldIncrementCountRef.current = !isCurrentUserReply;
        const newReplies = {
          ...prevReplies,
          [commentId]: sortRepliesChronologically([...existingReplies, replyData]),
        };
        repliesRef.current = newReplies;
        
        if (!isCurrentUserReply) {
          setSelectedMessage((prev) => {
            if (prev && prev.id.toString() === commentId) {
              const currentRepliesCount = prev.repliesCount !== undefined ? prev.repliesCount : 0;
              return {
                ...prev,
                repliesCount: currentRepliesCount + 1,
                lastReplyAt: replyCreatedAt,
              };
            }
            return prev;
          });
        }
        
        return newReplies;
      });

      setTimeout(() => {
        if (shouldIncrementCountRef.current) {
          setMessages((prev) => {
            const messageIndex = prev.findIndex((msg) => {
              const msgId = msg.id?.toString();
              const commentIdStr = commentId?.toString();
              return msgId === commentIdStr || 
                     msgId === commentId || 
                     msg.id === commentId ||
                     msg.id?.toString() === commentId?.toString();
            });

            if (messageIndex !== -1) {
              const updatedMessages = [...prev];
              const currentMessage = updatedMessages[messageIndex];
              const currentRepliesCount = currentMessage.repliesCount !== undefined ? currentMessage.repliesCount : 0;
              
              const currentRepliesInState = repliesRef.current[commentId] || [];
              const actualRepliesCount = currentRepliesInState.length;
              
              if (currentRepliesCount >= actualRepliesCount) {
                return prev;
              }

              updatedMessages[messageIndex] = {
                ...currentMessage,
                repliesCount: currentRepliesCount + 1,
                lastReplyAt: replyCreatedAt,
              };
              
              return updatedMessages;
            }

            return prev;
          });

          setSelectedMessage((prev) => {
            if (prev && prev.id.toString() === commentId) {
              const currentRepliesCount = prev.repliesCount !== undefined ? prev.repliesCount : 0;
              const currentRepliesInState = repliesRef.current[commentId] || [];
              const actualRepliesCount = currentRepliesInState.length;
              
              if (currentRepliesCount >= actualRepliesCount) {
                return prev;
              }

              return {
                ...prev,
                repliesCount: currentRepliesCount + 1,
                lastReplyAt: replyCreatedAt,
              };
            }
            return prev;
          });
        }
      }, 0);
    };

    possibleNewReplyEvents.forEach(eventName => {
      const handler = (data: any) => {
        handleNewReply(data);
      };
      eventHandlers[eventName] = handler;
      socket.on(eventName, handler);
    });

    return () => {
      isUnmountingRef.current = true;

      if (socket) {
        const chatBoardIdChanged = previousChatBoardId !== chatBoardId;

        if (socket.connected && chatBoardIdChanged && previousChatBoardId) {
          const leaveData = {
            action: "leave",
            chat_board_id: previousChatBoardId.toString(),
            board_id: previousChatBoardId.toString(),
          };
          socket.emit("chat_board", leaveData);
        }

        [...possibleNewCommentEvents, ...possibleNewReplyEvents].forEach(eventName => {
          if (eventHandlers[eventName]) {
            socket.off(eventName, eventHandlers[eventName]);
          }
        });

        socket.off("connect");
        socket.off("reconnect", handleReconnect);
        socket.off("disconnect");
      }
    };
  }, [chatBoardId, userId, setMessages, setReplies, setSelectedMessage, messagesContainerRef]);
  
  useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
      const socket = socketRef.current;

      if (socket && socket.connected && currentChatBoardIdRef.current) {
        const leaveData = {
          action: "leave",
          chat_board_id: currentChatBoardIdRef.current.toString(),
          board_id: currentChatBoardIdRef.current.toString(),
        };
        socket.emit("chat_board", leaveData);
      }
    };
  }, []);

  return { socketRef };
}

